import { useEffect, useState, useCallback } from "react";
import { BarChart2, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { pageKeyFromPath } from "@/lib/pageTracking";

export default function AdminStatsWidget() {
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const page = pageKeyFromPath(location.pathname);

  useEffect(() => {
    base44.auth.me().then(user => {
      if (user?.role === "admin") setIsAdmin(true);
    }).catch(() => {});
  }, []);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [sessions, clicks] = await Promise.all([
        base44.entities.ResourceEvent.filter({ page, event_type: "session" }, "-created_date", 500),
        base44.entities.ResourceEvent.filter({ page, event_type: "click" }, "-created_date", 500),
      ]);

      // Build last-14-day sessions chart
      const dayMap = {};
      for (let i = 13; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        dayMap[d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })] = 0;
      }
      sessions.forEach(e => {
        const key = new Date(e.created_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
        if (key in dayMap) dayMap[key]++;
      });
      const daily = Object.entries(dayMap).map(([date, count]) => ({ date, count }));

      setStats({ sessions: sessions.length, clicks: clicks.length, daily });
    } catch (_) {}
    setLoading(false);
  }, [page]);

  // Reload whenever the page changes (if widget is open) or when it's opened
  useEffect(() => {
    if (isAdmin && open) loadStats();
  }, [isAdmin, open, page, loadStats]);

  if (!isAdmin) return null;

  const pageName = page.replace("resource:", "").replace(/^\//, "") || "Home";

  return (
    <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9000, fontFamily: "'DM Sans', sans-serif" }}>
      {open ? (
        <div style={{ background: "#112215", borderRadius: 16, padding: "16px 18px", boxShadow: "0 12px 40px rgba(0,0,0,0.35)", border: "1px solid #2a2a2a", width: 240 }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <BarChart2 size={13} color="#4A6DC7" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", letterSpacing: "0.05em", textTransform: "uppercase" }}>This page</span>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex" }}>
              <X size={13} color="#555" />
            </button>
          </div>

          {/* Page name */}
          <p style={{ fontSize: 11, color: "#888", margin: "0 0 12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={page}>{pageName}</p>

          {/* Stat pills */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
            <div style={{ background: "#112215", borderRadius: 8, padding: "10px", textAlign: "center" }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: "#fff", margin: 0 }}>{loading ? "…" : (stats?.sessions ?? "—")}</p>
              <p style={{ fontSize: 9, color: "#666", margin: "3px 0 0", textTransform: "uppercase", letterSpacing: "0.07em" }}>Sessions</p>
            </div>
            <div style={{ background: "#112215", borderRadius: 8, padding: "10px", textAlign: "center" }}>
              <p style={{ fontSize: 22, fontWeight: 800, color: "#fff", margin: 0 }}>{loading ? "…" : (stats?.clicks ?? "—")}</p>
              <p style={{ fontSize: 9, color: "#666", margin: "3px 0 0", textTransform: "uppercase", letterSpacing: "0.07em" }}>Clicks</p>
            </div>
          </div>

          {/* Sparkline */}
          <p style={{ fontSize: 9, color: "#555", textTransform: "uppercase", letterSpacing: "0.07em", margin: "0 0 6px" }}>Sessions — last 14 days</p>
          {loading || !stats ? (
            <div style={{ height: 48, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 11, color: "#444" }}>Loading…</span>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={54}>
              <LineChart data={stats.daily}>
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#4A6DC7"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={true}
                />
                <Tooltip
                  contentStyle={{ background: "#112215", border: "none", borderRadius: 6, fontSize: 11, fontFamily: "'DM Sans', sans-serif", padding: "4px 8px" }}
                  labelStyle={{ color: "#888", fontSize: 10 }}
                  itemStyle={{ color: "#fff" }}
                  formatter={(v) => [v, "sessions"]}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          style={{ width: 40, height: 40, borderRadius: "50%", background: "#112215", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.25)", transition: "transform 0.2s" }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
          title="Page stats"
        >
          <BarChart2 size={16} color="#4A6DC7" />
        </button>
      )}
    </div>
  );
}